import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../../../../layoutcontainers/DashboardLayout";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import { useLoading } from "../../../../context/loadingcontext";
import axios from "axios";
import { BASE_URL, postData } from "../../../Utils/Baseurl";
import MaterialTypography from "../../../../components/MaterialTypography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MaterialInput from "../../../../components/MaterialInput";
import MaterialSelect from "../../../../components/MaterialSelect";
import MaterialBox from "../../../../components/MaterialBox";

const ProjectReportTabel = () => {
  const navigate = useNavigate();
  const { setCirleLoading } = useLoading();
  const [project, setProject] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const open = Boolean(anchorEl);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(null);

  const statusOptions = [
    { value: "0", label: "Not Started" },
    { value: "1", label: "In Progress" },
    { value: "2", label: "Completed" },
    { value: "3", label: "On Hold" },
    { value: "4", label: "Aborted" },
  ];

  /**
   * Fetches a summary of all projects from the API and updates the state with the retrieved data.
   * Retrieves the authentication token from session storage and includes it in the request headers.
   * Upon successful response, sets the project state with the fetched data.
   * Logs any errors encountered during the fetch operation.
   */
  const fetchProjectData = useCallback(async () => {
    setCirleLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(
        `${BASE_URL}api/all-projects-summary/?search=${search}&status=${status?.value || ""
        }`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = response.data.data;
      setProject(data);
    } catch (error) {
      console.log(error, "error");
    } finally { 
      setCirleLoading(false);
    }
  }, [search, status, setCirleLoading]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProjectData();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchProjectData]);

  /**
   * Deletes a project from the API based on the selectedProjectId.
   * Retrieves the authentication token from session storage and includes it in the request headers.
   * Upon successful response, logs the response data.
   * Logs any errors encountered during the delete operation.
   */

  const handleClick = (event, projectId) => {
    setAnchorEl(event.currentTarget);
    setSelectedProjectId(projectId);
  };

  const handleClose = async (item) => {
    if (item && selectedProjectId) {
      const formdata = new FormData();
      formdata.append("project_id", item);
      try {
        const data = await postData("api/updateActualEndDates/", formdata);
        console.log(data, "data");
      } catch (error) {
        console.log(error);
      }
      navigate(`/project-report/view-report`, { state: item });
    }

    setAnchorEl(null);
    setSelectedProjectId(null);
  };

  return (
    <DashboardLayout>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        p={1}
        mb={2}
      >
        <Stack direction="column" spacing={0}>
          <MaterialTypography variant="h5" fontWeight="medium" color="dark">
            Project
          </MaterialTypography>
          <MaterialTypography variant="caption" color="text">
            You can view the projects here.
          </MaterialTypography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: "200px" }}>
            <MaterialInput
              type="text"
              label="Search"
              placeholder="Search Project"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box sx={{ width: "200px" }}>
            <MaterialSelect
              size="small"
              placeholder="Select Status"
              options={statusOptions}
              value={status}
              onChange={(selectedOption) => setStatus(selectedOption)}
              isClearable
            />
          </Box>
        </Stack>
      </Stack>

      <Grid container spacing={1} sx={{ mt: 2, p: 2 }}>
        {project.map((item) => (
          <Grid size={{ xs: 12, sm: 4, md: 2.4, lg: 2.4 }} key={item.project_id}>
            <Card
              sx={{
                borderRadius: "24px",
                overflow: "visible",
                position: "relative",
                boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
            >
              <IconButton
                onClick={(event) => handleClick(event, item.project_id)}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  width: 30,
                  height: 30,
                  zIndex: 1,
                }}
              >
                <MoreVertIcon sx={{ color: "#333", fontSize: "0.7rem" }} />
              </IconButton>
              <Box sx={{ px: 1.5, pb: 1 }}>
                <Box sx={{ borderTop: "1px solid #f0f0f0", pt: 1, mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Avatar
                      src={`${BASE_URL}${item.project_logo}`}
                      sx={{
                        width: 45,
                        height: 45,
                        borderRadius: "50%",
                        border: "2px solid #fff",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        backgroundColor: "#f0f0f0",
                      }}
                    />
                    <Box>
                      <Tooltip
                        title={item.project_name}
                        disableHoverListener={item.project_name?.length <= 15}
                      >
                        <MaterialTypography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                            color: "#1a1a1a",
                            fontSize: "0.8rem",
                            mb: 0.4,
                            cursor: "pointer",
                          }}
                        >
                          {item.project_name?.length > 15
                            ? `${item.project_name.substring(0, 15)}...`
                            : item.project_name}
                        </MaterialTypography>
                      </Tooltip>
                      <MaterialTypography
                        variant="body2"
                        color="text"
                        sx={{ fontSize: "0.8rem", mb: 0.4 }}
                      >
                        {item.project_code}
                      </MaterialTypography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      backgroundColor: "#f5f8ff",
                      borderRadius: "20px",
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <MaterialTypography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ color: "#333", mb: 1, fontSize: "0.9rem" }}
                    >
                      Projects Metrics
                    </MaterialTypography>
                    <Stack spacing={1}>
                      <Box>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={0.5}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <MaterialTypography
                              variant="caption"
                              fontWeight="bold"
                              sx={{ color: "#666" }}
                            >
                              Progress
                            </MaterialTypography>
                          </Stack>
                          <MaterialTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: "#1a1a1a" }}
                          >
                            {item.completion_percent}%
                          </MaterialTypography>
                        </Stack>
                        <Box
                          sx={{
                            width: "100%",
                            height: 6,
                            backgroundColor: "#c0d2fbff",
                            borderRadius: 3,
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${item.completion_percent}%`,
                              height: "100%",
                              backgroundColor: "#7ca3fdff",
                              borderRadius: 3,
                              transition: "width 0.5s ease-in-out",
                            }}
                          />
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <MaterialTypography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ color: "#666" }}
                        >
                          Days Left :
                        </MaterialTypography>
                        <MaterialTypography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ color: "#1a1a1a" }}
                        >
                          {item.days_left}
                        </MaterialTypography>
                      </Stack>
                    </Stack>
                  </Box>

                  <Stack spacing={1} mb={1}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MaterialTypography
                          variant="caption"
                          sx={{ color: "#666", fontWeight: 500 }}
                        >
                          Manager
                        </MaterialTypography>
                      </Stack>
                      <MaterialTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: "#333" }}
                      >
                        {item.manager || "Not Assigned"}
                      </MaterialTypography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MaterialTypography
                          variant="caption"
                          sx={{ color: "#666", fontWeight: 500 }}
                        >
                          Start Date
                        </MaterialTypography>
                      </Stack>
                      <MaterialTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: "#333" }}
                      >
                        {item.start_date || "N/A"}
                      </MaterialTypography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MaterialTypography
                          variant="caption"
                          sx={{ color: "#666", fontWeight: 500 }}
                        >
                          End Date
                        </MaterialTypography>
                      </Stack>
                      <MaterialTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: "#333" }}
                      >
                        {item.end_date || "N/A"}
                      </MaterialTypography>
                    </Box>
                  </Stack>

                  {(() => {
                    const getStatusStyle = (status) => {
                      const s = status?.toLowerCase() || "";
                      if (
                        s.includes("track") ||
                        s.includes("on track") ||
                        s.includes("completed")
                      )
                        return { bg: "#e6f9f1", color: "#10b981" };
                      if (s.includes("delayed") || s.includes("abort"))
                        return { bg: "#fff4e5", color: "#f5590bff" };
                      if (s.includes("completed") || s.includes("finished"))
                        return { bg: "#e0f2fe", color: "#0ea5e9" };
                      if (s.includes("hold"))
                        return { bg: "#f3f4f6", color: "#f6cf32ff" };
                      if (s.includes("not started"))
                        return { bg: "#fef2f2", color: "#f5946bff" };
                      if (s.includes("in progress"))
                        return { bg: "#f5f3ff", color: "#8b5cf6" };
                      return { bg: "#f5f8ff", color: "#5243f5" };
                    };
                    const style = getStatusStyle(item.status);
                    return (
                      <MaterialBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "30px",
                          width: "100%",
                          height: "30px",
                          backgroundColor: style.bg,
                          color: style.color,
                          textTransform: "none",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          px: 2,
                          mt: 1,
                        }}
                      >
                        {item.status || "N/A"}
                      </MaterialBox>
                    );
                  })()}
                </Box>
              </Box>
              <Menu
                anchorEl={anchorEl}
                open={open && selectedProjectId === item.project_id}
                onClose={() => handleClose("")}
                elevation={3}
                sx={{
                  "& .MuiPaper-root": {
                    borderRadius: "12px",
                    minWidth: 90,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  },
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <MenuItem
                  onClick={() => handleClose(item.project_id)}
                  sx={{ py: 1, gap: 1 }}
                >
                  View Report
                </MenuItem>
              </Menu>
            </Card>
          </Grid>
        ))}
      </Grid>
    </DashboardLayout>
  );
};

export default ProjectReportTabel;
